import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapEmpty } from "@/shared/api/empty";
import { uploadFile } from "@/shared/api/files";
import { makeKeys } from "@/shared/api/queryKeys";

export type DayStatus = Schemas["DayStatus"];
export type DayCell = Schemas["DayCellOut"];
export type DayPlan = Schemas["DayPlan"];
export type GridRow = Schemas["GridRowOut"];
export type Grid = Schemas["GridOut"];
export type DayDetail = Schemas["DayDetailOut"];
export type DayAdjustment = Schemas["DayAdjustmentOut"];
export type AdjustmentPatch = Schemas["AdjustmentPatch"];
export type StatusOverride = Schemas["StatusOverride"];
export type ManualDeduction = Schemas["ManualDeduction"];
export type DeductionSource = Schemas["DeductionSource"];
export type ReviewReason = Schemas["ReviewReason"];
export type ReviewItem = Schemas["ReviewItemOut"];
export type Period = Schemas["PeriodOut"];
export type PeriodStatus = Schemas["PeriodStatus"];
export type Policy = Schemas["PolicyOut"];
export type PolicyIn = Schemas["PolicyIn"];
export type Tier = Schemas["TierOut"];
export type DeductionUnit = Schemas["DeductionUnit"];
export type WeeklyPlan = Schemas["WeeklyPlanOut"];
export type WeeklyPlanIn = Schemas["WeeklyPlanIn"];
export type WorkSession = Schemas["WorkSessionOut"];
export type SessionMode = Schemas["SessionMode"];
export type SessionCreate = Schemas["SessionCreate"];
export type ImportRun = Schemas["ImportOut"];
export type ImportMapping = Schemas["ImportMappingIn"];
export type Today = Schemas["TodayOut"];
export type AttendanceRequest = Schemas["AttendanceRequestOut"];
export type AttendanceRequestCreate = Schemas["AttendanceRequestCreate"];
export type RequestKind = Schemas["RequestKind"];
export type PermissionEdge = Schemas["PermissionEdge"];
export type PermissionMode = Schemas["PermissionMode"];
export type PermissionUsage = Schemas["PermissionUsageOut"];
export type Break = Schemas["BreakOut"];
export type BreakIn = Schemas["BreakIn"];
export type DayChange = Schemas["DayChangeOut"];
export type Presence = Schemas["Presence"];
export type PresenceRow = Schemas["PresenceOut"];

export const REQUEST_KINDS: readonly RequestKind[] = [
  "permission",
  "external_meeting",
  "work_from_home",
  "punch_correction",
];

export const attendanceKeys = makeKeys("attendance");
const gridKey = (month: string, department: string | null) =>
  [...attendanceKeys.lists(), "grid", { month, department }] as const;
const personMonthKey = (employeeId: string, month: string) =>
  [...attendanceKeys.lists(), "person", employeeId, month] as const;
const myMonthKey = (month: string) => [...attendanceKeys.lists(), "mine", month] as const;
const reviewKey = (month: string) => [...attendanceKeys.lists(), "review", month] as const;
const periodKey = (month: string) => [...attendanceKeys.all, "period", month] as const;
const dayKey = (employeeId: string, day: string) =>
  [...attendanceKeys.details(), employeeId, day] as const;
const policyKey = [...attendanceKeys.all, "policy"] as const;
const defaultPlanKey = [...attendanceKeys.all, "weekly-plan"] as const;
const employeePlanKey = (employeeId: string) =>
  [...attendanceKeys.all, "weekly-plan", employeeId] as const;
const departmentPlanKey = (departmentId: string) =>
  [...attendanceKeys.all, "weekly-plan", "department", departmentId] as const;
const presenceKey = [...attendanceKeys.all, "presence"] as const;
const usageKey = (month: string) => [...attendanceKeys.all, "permission-usage", month] as const;
const importsKey = [...attendanceKeys.all, "imports"] as const;
export const todayKey = [...attendanceKeys.all, "today"] as const;
export const myRequestsKey = [...attendanceKeys.all, "my-requests"] as const;

/** Everything a change to days can move: grids, months, the queue, periods and open drawers. */
function refreshDays(queryClient: QueryClient): Promise<unknown> {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === attendanceKeys.all[0] &&
      !["policy", "weekly-plan", "imports"].includes(String(query.queryKey[1])),
  });
}

/* ---- The month ---- */

export function useGrid(month: string, department: string | null) {
  return useQuery({
    queryKey: gridKey(month, department),
    queryFn: () =>
      unwrap(api.GET("/api/v1/attendance/grid", { params: { query: { month, department } } })),
  });
}

export function usePersonMonth(employeeId: string, month: string) {
  return useQuery({
    queryKey: personMonthKey(employeeId, month),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/attendance/employees/{employee_id}/month", {
          params: { path: { employee_id: employeeId }, query: { month } },
        }),
      ),
  });
}

export function useMyMonth(month: string) {
  return useQuery({
    queryKey: myMonthKey(month),
    queryFn: () => unwrap(api.GET("/api/v1/me/attendance", { params: { query: { month } } })),
  });
}

export function useReview(month: string) {
  return useQuery({
    queryKey: reviewKey(month),
    queryFn: () => unwrap(api.GET("/api/v1/attendance/review", { params: { query: { month } } })),
  });
}

export function usePeriod(month: string) {
  return useQuery({
    queryKey: periodKey(month),
    queryFn: () =>
      unwrap(api.GET("/api/v1/attendance/periods/{month}", { params: { path: { month } } })),
  });
}

/** Close the month for review, or open it again. Pessimistic: the status is the server's. */
export function usePeriodTransition(month: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: "finalize" | "reopen") =>
      action === "finalize"
        ? unwrap(
            api.POST("/api/v1/attendance/periods/{month}/finalize", {
              params: { path: { month } },
            }),
          )
        : unwrap(
            api.POST("/api/v1/attendance/periods/{month}/reopen", { params: { path: { month } } }),
          ),
    onSuccess: (period) => {
      queryClient.setQueryData(periodKey(month), period);
      return refreshDays(queryClient);
    },
  });
}

/* ---- One day ---- */

export function useDayDetail(employeeId: string, day: string, enabled = true) {
  return useQuery({
    queryKey: dayKey(employeeId, day),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/attendance/employees/{employee_id}/days/{day}", {
          params: { path: { employee_id: employeeId, day } },
        }),
      ),
    enabled,
  });
}

export interface DayAdjust {
  patch: AdjustmentPatch;
  /** The adjustment's version the drawer showed; 0 for a day HR never touched. */
  version: number;
}

/** Change HR's layer on a day. Pessimistic: the drawer waits for the recomputed day. */
export function useAdjustDay(employeeId: string, day: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patch, version }: DayAdjust) =>
      unwrap(
        api.PATCH("/api/v1/attendance/employees/{employee_id}/days/{day}", {
          params: {
            path: { employee_id: employeeId, day },
            header: { "if-match": String(version) },
          },
          body: patch,
        }),
      ),
    onSuccess: (detail) => {
      queryClient.setQueryData(dayKey(employeeId, day), detail);
      return refreshDays(queryClient);
    },
  });
}

/** Switch the shortfall deduction off for many days with one reason (an outage, a power cut). */
export function useBulkExempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["BulkExemptIn"]) =>
      unwrap(api.POST("/api/v1/attendance/days/bulk-exempt", { body })),
    onSuccess: () => refreshDays(queryClient),
  });
}

/* ---- HR's session corrections ---- */

export function useAddSession(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SessionCreate) =>
      unwrap(
        api.POST("/api/v1/attendance/employees/{employee_id}/sessions", {
          params: { path: { employee_id: employeeId } },
          body,
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ session, body }: { session: WorkSession; body: SessionCreate }) =>
      unwrap(
        api.PATCH("/api/v1/attendance/sessions/{session_id}", {
          params: {
            path: { session_id: session.id },
            header: { "if-match": String(session.version) },
          },
          body,
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}

export function useRemoveSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      unwrapEmpty(
        api.DELETE("/api/v1/attendance/sessions/{session_id}", {
          params: { path: { session_id: sessionId } },
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}

/* ---- HR's break corrections ---- */

export function useAddBreak(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BreakIn) =>
      unwrap(
        api.POST("/api/v1/attendance/employees/{employee_id}/breaks", {
          params: { path: { employee_id: employeeId } },
          body,
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}

export function useUpdateBreak() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entry, body }: { entry: Break; body: BreakIn }) =>
      unwrap(
        api.PUT("/api/v1/attendance/breaks/{break_id}", {
          params: {
            path: { break_id: entry.id },
            header: { "if-match": String(entry.version) },
          },
          body,
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}

export function useRemoveBreak() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ breakId, reason }: { breakId: string; reason: string }) =>
      unwrapEmpty(
        api.POST("/api/v1/attendance/breaks/{break_id}/remove", {
          params: { path: { break_id: breakId } },
          body: { reason },
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}

/* ---- Who is in right now ---- */

/** Refreshed every minute: a board of who is working, on a break, on leave or off. */
const PRESENCE_REFRESH_MS = 60_000;

export function useTeamPresence() {
  return useQuery({
    queryKey: presenceKey,
    queryFn: () => unwrap(api.GET("/api/v1/attendance/presence")),
    refetchInterval: PRESENCE_REFRESH_MS,
    refetchIntervalInBackground: false,
    // A 403 (neither HR nor a manager) is an answer: asking again would not change it.
    retry: false,
  });
}

/* ---- Rules: the policy and the weekly plans ---- */

export function usePolicyHistory(enabled = true) {
  return useQuery({
    queryKey: policyKey,
    queryFn: () => unwrap(api.GET("/api/v1/attendance/policy")),
    enabled,
  });
}

export function useSavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PolicyIn) => unwrap(api.PUT("/api/v1/attendance/policy", { body })),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: policyKey }),
        refreshDays(queryClient),
      ]),
  });
}

export function useDefaultPlan() {
  return useQuery({
    queryKey: defaultPlanKey,
    queryFn: () => unwrap(api.GET("/api/v1/attendance/weekly-plan")),
  });
}

export function useEmployeePlan(employeeId: string) {
  return useQuery({
    queryKey: employeePlanKey(employeeId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}/weekly-plan", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
  });
}

export function useDepartmentPlan(departmentId: string | null) {
  return useQuery({
    queryKey: departmentPlanKey(departmentId ?? ""),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/attendance/departments/{department_id}/weekly-plan", {
          params: { path: { department_id: departmentId ?? "" } },
        }),
      ),
    enabled: departmentId !== null,
  });
}

/** Whose week is saved: the agency's default, one department's, or one person's own. */
export type PlanOwner =
  { kind: "default" } | { kind: "department"; id: string } | { kind: "employee"; id: string };

function savePlan(owner: PlanOwner, body: WeeklyPlanIn) {
  switch (owner.kind) {
    case "default":
      return unwrap(api.PUT("/api/v1/attendance/weekly-plan", { body }));
    case "department":
      return unwrap(
        api.PUT("/api/v1/attendance/departments/{department_id}/weekly-plan", {
          params: { path: { department_id: owner.id } },
          body,
        }),
      );
    case "employee":
      return unwrap(
        api.PUT("/api/v1/employees/{employee_id}/weekly-plan", {
          params: { path: { employee_id: owner.id } },
          body,
        }),
      );
  }
}

/** Save a week from a date: the agency's default week, a department's, or one person's own. */
export function useSavePlan(owner: PlanOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WeeklyPlanIn) => savePlan(owner, body),
    onSuccess: () =>
      Promise.all([
        // The default week's key starts every week's key: departments and people refresh too.
        queryClient.invalidateQueries({ queryKey: defaultPlanKey }),
        refreshDays(queryClient),
      ]),
  });
}

/* ---- Device imports ---- */

export function useImports() {
  return useQuery({
    queryKey: importsKey,
    queryFn: () => unwrap(api.GET("/api/v1/attendance/imports")),
  });
}

export function useUploadImport() {
  return useMutation({
    mutationFn: (file: File) =>
      uploadFile<ImportRun>("/api/v1/attendance/imports", file, { method: "POST" }),
  });
}

export function useCommitImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ importId, mapping }: { importId: string; mapping: ImportMapping }) =>
      unwrap(
        api.POST("/api/v1/attendance/imports/{import_id}/commit", {
          params: { path: { import_id: importId } },
          body: mapping,
        }),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: importsKey }),
        refreshDays(queryClient),
      ]),
  });
}

/* ---- My day: clocking in and out ---- */

/** Refreshes now and then so a clock left open on another device shows here too. */
const TODAY_REFRESH_MS = 60_000;

export function useToday(enabled = true) {
  return useQuery({
    queryKey: todayKey,
    queryFn: () => unwrap(api.GET("/api/v1/me/attendance/today")),
    enabled,
    refetchInterval: TODAY_REFRESH_MS,
    refetchIntervalInBackground: false,
    // No record (404) is an answer, not a hiccup: asking again would not change it.
    retry: false,
  });
}

/** Clock in or out now. Pessimistic: the chip shows the session the server recorded. */
export function useClock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: "in" | "out") =>
      action === "in"
        ? unwrap(api.POST("/api/v1/me/clock-in", { body: {} }))
        : unwrap(api.POST("/api/v1/me/clock-out")),
    onSuccess: () => refreshDays(queryClient),
  });
}

/** Start or end a break while clocked in. The chip shows the day the server recorded. */
export function useBreak() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: "start" | "end") =>
      action === "start"
        ? unwrap(api.POST("/api/v1/me/breaks/start", { body: { kind: "regular" } }))
        : unwrap(api.POST("/api/v1/me/breaks/end")),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: todayKey }),
  });
}

/* ---- My attendance requests ---- */

/** My permissions this month against the policy's limit, for the request form. */
export function usePermissionUsage(month: string | null) {
  return useQuery({
    queryKey: usageKey(month ?? ""),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/me/permissions/usage", { params: { query: { month: month ?? "" } } }),
      ),
    enabled: month !== null,
  });
}

export function useMyRequests() {
  return useQuery({
    queryKey: myRequestsKey,
    queryFn: () => unwrap(api.GET("/api/v1/me/attendance-requests")),
  });
}

export function useCreateRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AttendanceRequestCreate) =>
      unwrap(api.POST("/api/v1/me/attendance-requests", { body })),
    onSuccess: (created) => {
      queryClient.setQueryData<AttendanceRequest[]>(myRequestsKey, (list) =>
        list ? [created, ...list] : list,
      );
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: myRequestsKey }),
        queryClient.invalidateQueries({ queryKey: [...attendanceKeys.all, "permission-usage"] }),
      ]);
    },
  });
}

export function useCancelRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      unwrap(
        api.POST("/api/v1/attendance-requests/{request_id}/cancel", {
          params: { path: { request_id: requestId } },
        }),
      ),
    onSuccess: () => refreshDays(queryClient),
  });
}
